import { useEffect, useState } from 'react';
import { Api } from '../application/panelApi';
import { confirmDialog } from '../components/ModalHost';
import { toast } from '../components/ToastHost';
import { errText } from '../lib/format';
import { useApp, type SettingsPayload } from '../store/AppContext';

const LOCK_FARM_TITLE =
  'Сейчас подготовкой профилей управляет страница «Запуск». Чтобы менять настройку здесь, выключите там управление.';
const LOCK_RESET_TITLE = 'Сброс недоступен, пока включено управление на странице «Запуск». Сначала выключите его там.';
const MAX_BATCH_BYTES = 400_000;
const MAX_SINGLE_COOKIE_TEXT = 350_000;
const PRIORITY_COOKIE_HOST = /(?:bing|microsoft|live|msn|google|youtube|gstatic)\./i;

function compactCookieContent(raw: string): string {
  const clean = raw.replace(/^\uFEFF/, '').trim();
  if (clean.length <= MAX_SINGLE_COOKIE_TEXT) return clean;
  if (clean.startsWith('[')) {
    try {
      const parsed = JSON.parse(clean) as Array<Record<string, unknown>>;
      if (Array.isArray(parsed)) {
        const lines: string[] = [];
        for (const row of parsed) {
          if (!row || typeof row !== 'object') continue;
          const domain = String(row.domain ?? '').trim();
          const name = String(row.name ?? '').trim();
          if (!domain || !name) continue;
          const path = String(row.path ?? '/') || '/';
          const secure = row.secure ? 'TRUE' : 'FALSE';
          const exp = Math.max(0, Math.floor(Number(row.expirationDate ?? row.expires ?? 0) || 0));
          const val = String(row.value ?? '').replace(/[\r\n\t]+/g, '');
          lines.push(`${domain}\tTRUE\t${path}\t${secure}\t${exp}\t${name}\t${val}`);
        }
        return trimCookieLines(lines);
      }
    } catch {
      /* fall through to line trimming */
    }
  }
  return trimCookieLines(clean.split(/\r?\n/));
}

function trimCookieLines(lines: string[]): string {
  const priority: string[] = [];
  const rest: string[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || (trimmed.startsWith('#') && !/^#HttpOnly_/i.test(trimmed))) continue;
    if (PRIORITY_COOKIE_HOST.test(trimmed)) priority.push(trimmed);
    else rest.push(trimmed);
  }
  const out: string[] = [];
  let bytes = 0;
  for (const line of [...priority, ...rest]) {
    if (bytes + line.length + 1 > MAX_SINGLE_COOKIE_TEXT) break;
    out.push(line);
    bytes += line.length + 1;
  }
  return out.join('\n');
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate-raw');
  const writer = ds.writable.getWriter();
  const ab = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
  void writer.write(new Uint8Array(ab));
  void writer.close();
  const res = new Response(ds.readable);
  return new Uint8Array(await res.arrayBuffer());
}

async function extractZipCookieFiles(file: File): Promise<Array<{ name: string; content: string }>> {
  const buf = new Uint8Array(await file.arrayBuffer());
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const decoder = new TextDecoder('utf-8');
  let eocd = -1;
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 65_557); i--) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) return [];
  const cdCount = view.getUint16(eocd + 10, true);
  let cdOffset = view.getUint32(eocd + 16, true);
  const out: Array<{ name: string; content: string }> = [];
  for (let idx = 0; idx < cdCount && cdOffset + 46 <= buf.length; idx++) {
    if (view.getUint32(cdOffset, true) !== 0x02014b50) break;
    const method = view.getUint16(cdOffset + 10, true);
    const compSize = view.getUint32(cdOffset + 20, true);
    const nameLen = view.getUint16(cdOffset + 28, true);
    const extraLen = view.getUint16(cdOffset + 30, true);
    const commentLen = view.getUint16(cdOffset + 32, true);
    const localOffset = view.getUint32(cdOffset + 42, true);
    const fullName = decoder.decode(buf.subarray(cdOffset + 46, cdOffset + 46 + nameLen));
    cdOffset += 46 + nameLen + extraLen + commentLen;
    if (fullName.endsWith('/') || !fullName.toLowerCase().endsWith('.txt')) continue;
    if (localOffset + 30 > buf.length || view.getUint32(localOffset, true) !== 0x04034b50) continue;
    const lNameLen = view.getUint16(localOffset + 26, true);
    const lExtraLen = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + lNameLen + lExtraLen;
    const slice = buf.subarray(dataStart, dataStart + compSize);
    try {
      const rawBytes = method === 0 ? slice : method === 8 ? await inflateRaw(slice) : null;
      if (!rawBytes) continue;
      const baseName = fullName.split('/').pop() || 'cookies.txt';
      out.push({ name: baseName, content: decoder.decode(rawBytes) });
    } catch {
      /* skip unreadable entry */
    }
  }
  return out;
}

const PACE_ZONES = [
  { id: 'Europe/Moscow', label: 'Москва (UTC+3)' },
  { id: 'Europe/Kyiv', label: 'Киев' },
  { id: 'Europe/Minsk', label: 'Минск (UTC+3)' },
  { id: 'Asia/Yekaterinburg', label: 'Екатеринбург (UTC+5)' },
  { id: 'Asia/Almaty', label: 'Алматы (UTC+5)' },
  { id: 'Asia/Tbilisi', label: 'Тбилиси (UTC+4)' },
  { id: 'Europe/Berlin', label: 'Берлин' },
  { id: 'UTC', label: 'UTC' },
] as const;

type Form = {
  concurrency: string;
  ramPerBrowser: string;
  farmSplitPct: string;
  continuousFarm: boolean;
  clickPaceEnabled: boolean;
  clickPaceTimezone: string;
  clickPaceStrength: string;
  clickPaceStartHour: string;
  clickPaceEndHour: string;
  tgToken: string;
  tgChatId: string;
  tgUsers: string;
  notifyCritical: boolean;
};

function formFromPayload(data: SettingsPayload | null): Form {
  const s = data?.settings || {};
  return {
    concurrency: String(data?.deploy?.workerConcurrency ?? 10),
    ramPerBrowser: String(data?.deploy?.ramPerBrowserMb ?? 350),
    farmSplitPct: String(s.farmSplitPct ?? 50),
    continuousFarm: Boolean(data?.deploy?.continuousFarm),
    clickPaceEnabled: s.clickPaceEnabled !== false,
    clickPaceTimezone: s.clickPaceTimezone || 'Europe/Moscow',
    clickPaceStrength: String(s.clickPaceStrength ?? 55),
    clickPaceStartHour: String(s.clickPaceStartHour ?? 0),
    clickPaceEndHour: String(s.clickPaceEndHour ?? 24),
    tgToken: '',
    tgChatId: s.telegramChatId ?? '',
    tgUsers: (s.telegramAllowedUserIds || []).join(', '),
    notifyCritical: s.notifyCritical !== false,
  };
}

function pacePreviewHeights(strength: number, start: number, end: number): number[] {
  const t = Math.min(100, Math.max(0, strength)) / 100;
  const bars: number[] = [];
  for (let h = 0; h < 24; h += 1) {
    const hour = h + 0.5;
    const open = start === end || (start === 0 && end >= 24)
      ? true
      : start < end ? hour >= start && hour < end : hour >= start || hour < end;
    if (!open) {
      bars.push(0.04 * (1 - t));
      continue;
    }
    const floor = 1 - 0.92 * t;
    const peak = (mu: number, sigma: number) => {
      let d = Math.abs(hour - mu);
      if (d > 12) d = 24 - d;
      return Math.exp(-(d * d) / (2 * sigma * sigma));
    };
    bars.push(floor + t * (peak(14, 3.2) + peak(19.5, 2.1) * 0.72));
  }
  const max = Math.max(...bars, 0.01);
  return bars.map((v) => Math.round(10 + 90 * (v / max)));
}

export function SettingsPage() {
  const { settingsPayload, refreshSettings, refreshAutopilot, refreshTasks, autopilotLocked, audit, user, ui } =
    useApp();

  const [form, setForm] = useState<Form>(() => formFromPayload(settingsPayload));
  const [pw, setPw] = useState({ old: '', next: '', again: '' });
  const [tgStatus, setTgStatus] = useState('');
  const [tgBusy, setTgBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cookieSummary, setCookieSummary] = useState<{ unused: number; used: number } | null>(null);
  const [cookieBusy, setCookieBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const activeTab = ui.subpage === 'telegram' || ui.subpage === 'password' || ui.subpage === 'reset'
    ? ui.subpage : 'nodes';

  const patchForm = (p: Partial<Form>) => setForm((f) => ({ ...f, ...p }));


  useEffect(() => {
    void refreshSettings().catch(() => undefined);
    void refreshAutopilot();
    void Api.clickCookies.summary().then(setCookieSummary).catch(() => undefined);
  }, [refreshSettings, refreshAutopilot]);

  const uploadCookies = async (files: File[]) => {
    if (!files.length) return;
    setCookieBusy(true);
    try {
      let last = cookieSummary;
      let added = 0;
      let skipped = 0;
      let batch: Array<{ name: string; content: string }> = [];
      let batchBytes = 0;
      const flush = async () => {
        if (!batch.length) return;
        const current = batch;
        batch = [];
        batchBytes = 0;
        const res = await Api.clickCookies.upload({ items: current });
        added += res.added ?? 0;
        skipped += res.skipped ?? 0;
        last = { unused: res.unused, used: res.used };
        setCookieSummary(last);
      };
      for (const file of files) {
        if (file.name.toLowerCase().endsWith('.zip')) {
          const zipEntries = await extractZipCookieFiles(file);
          for (const entry of zipEntries) {
            const content = compactCookieContent(entry.content);
            if (!content) continue;
            if (batch.length >= 20 || (batch.length > 0 && batchBytes + content.length > MAX_BATCH_BYTES)) {
              await flush();
            }
            batch.push({ name: entry.name, content });
            batchBytes += content.length;
          }
          continue;
        }
        const content = compactCookieContent(await file.text());
        if (batch.length >= 20 || (batch.length > 0 && batchBytes + content.length > MAX_BATCH_BYTES)) {
          await flush();
        }
        batch.push({ name: file.name, content });
        batchBytes += content.length;
      }
      await flush();
      if (added === 0 && skipped > 0) {
        toast('В выбранных файлах не найдено валидных куки.', 'err');
      } else if (skipped > 0) {
        toast(`Загружено файлов: ${added} (пропущено: ${skipped}).`, 'ok');
      } else {
        toast(`Загружено файлов куки: ${added}.`, 'ok');
      }
    } catch (error) {
      toast(errText(error), 'err');
    } finally {
      setCookieBusy(false);
    }
  };


  useEffect(() => {
    setForm(formFromPayload(settingsPayload));
  }, [settingsPayload]);

  const tokenSet = Boolean(settingsPayload?.settings?.telegramBotTokenSet);


  const readSettingsPatch = () => {
    const rawSplit = Number(form.farmSplitPct);
    const farmSplitPct = Number.isFinite(rawSplit) ? Math.max(0, Math.min(100, Math.round(rawSplit))) : 50;
    const strength = Number(form.clickPaceStrength);
    const startHour = Number(form.clickPaceStartHour);
    const endHour = Number(form.clickPaceEndHour);
    return {
      telegramChatId: form.tgChatId.trim(),
      telegramBotToken: form.tgToken.trim() || undefined,
      notifyCritical: form.notifyCritical,
      farmSplitPct,
      clickPaceEnabled: form.clickPaceEnabled,
      clickPaceTimezone: form.clickPaceTimezone,
      clickPaceStrength: Number.isFinite(strength) ? Math.max(0, Math.min(100, Math.round(strength))) : 55,
      clickPaceStartHour: Number.isFinite(startHour) ? Math.max(0, Math.min(23, Math.round(startHour))) : 0,
      clickPaceEndHour: Number.isFinite(endHour) ? Math.max(0, Math.min(24, Math.round(endHour))) : 24,
      telegramAllowedUserIds: form.tgUsers
        .split(/[\s,;]+/)
        .map((v) => v.trim())
        .filter(Boolean),
    };
  };

  const save = async () => {
    setSaving(true);
    try {
      const cc = form.concurrency;
      const rpb = form.ramPerBrowser;
      await Api.settings.save({
        settings: readSettingsPatch(),
        workerConcurrency: cc === '' ? undefined : +cc,
        ramPerBrowserMb: rpb ? +rpb : undefined,
        continuousFarm: autopilotLocked ? undefined : form.continuousFarm,
      });
      await refreshSettings();
      audit('Сохранил настройки', 'уведомления');
      toast('Настройки сохранены.', 'ok');
    } catch (err) {
      toast(errText(err), 'err');
    } finally {
      setSaving(false);
    }
  };

  const testTelegram = async () => {
    setTgBusy(true);
    setTgStatus('отправляю…');
    try {
      await Api.settings.save({ settings: readSettingsPatch() });
      const res = await Api.settings.telegramTest();
      await refreshSettings();
      setTgStatus(`отправлено в ${res.chatId || 'чат'}`);
      toast('Тестовое сообщение отправлено. Проверьте, что оно пришло в Telegram.', 'ok');
    } catch (err) {
      setTgStatus('');
      toast(errText(err), 'err');
    } finally {
      setTgBusy(false);
    }
  };

  const changePassword = async () => {
    const { old: current, next, again } = pw;
    if (!current) {
      toast('Введите старый пароль — без него сменить пароль нельзя.', 'err');
      return;
    }
    if (next.length < 8) {
      toast('Новый пароль слишком короткий. Используйте не меньше 8 символов.', 'err');
      return;
    }
    if (next !== again) {
      toast('Новый пароль и его повтор не совпадают. Введите их ещё раз.', 'err');
      return;
    }
    setPwBusy(true);
    try {
      const result = await Api.settings.password({ current, next });
      setPw({ old: '', next: '', again: '' });
      audit('Сменил пароль панели', user || '—');
      toast(
        result.ephemeral
          ? 'Пароль изменён, но только до перезапуска панели: на сервере задана переменная PANEL_PASSWORD. Чтобы изменение сохранилось, обновите её.'
          : 'Пароль изменён.',
        'ok',
      );
    } catch (err) {
      toast(errText(err), 'err');
    } finally {
      setPwBusy(false);
    }
  };

  const resetStats = () => {
    if (autopilotLocked) {
      toast('Статистику нельзя сбросить, пока включено управление на странице «Запуск». Выключите его и повторите.', 'wa');
      return;
    }
    confirmDialog(
      'Сбросить статистику?',
      'Счётчики успехов и ошибок по всем задачам обнулятся в панели и на всех нодах, которые сейчас на связи. Вернуть прежние значения будет нельзя.',
      async () => {
        try {
          const r = await Api.settings.resetStats();
          if (r.failed?.length) {
            toast(`Не удалось сбросить статистику на нодах: ${r.failed.join(', ')}. Проверьте, что они на связи, и повторите.`, 'err');
            return;
          }
          audit('Сбросил статистику', `${r.reset ?? 0} задач`);
          toast('Статистика сброшена.', 'ok');
          await refreshTasks().catch(() => undefined);
        } catch (err) {
          toast(errText(err), 'err');
        }
      },
      { yes: 'Сбросить' },
    );
  };

  return (
    <section id="page-settings" data-page="settings">
      <div className="page-head">
        <div>
          <h1>Настройки</h1>
        </div>
        {(activeTab === 'nodes' || activeTab === 'telegram') && (
          <div className="actions">
            <button className="btn btn-primary" id="saveSettings" type="button" onClick={() => void save()} disabled={saving}>
              Сохранить
            </button>
          </div>
        )}
      </div>

      <div className="settings">
        {activeTab === 'password' && <div className="sec">
          <h2>
            <span className="sq" />
            Пароль
          </h2>
          <div className="field-row">
            <label htmlFor="pwOld">Старый пароль</label>
            <input
              type="password"
              id="pwOld"
              placeholder="Введите текущий пароль"
              autoComplete="current-password"
              value={pw.old}
              onChange={(e) => setPw((p) => ({ ...p, old: e.target.value }))}
            />
          </div>
          <div className="field-row">
            <label htmlFor="pwNew">Новый пароль</label>
            <input
              type="password"
              id="pwNew"
              placeholder="Не меньше 8 символов"
              autoComplete="new-password"
              value={pw.next}
              onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
            />
          </div>
          <div className="field-row">
            <label htmlFor="pwNew2">Новый пароль ещё раз</label>
            <input
              type="password"
              id="pwNew2"
              placeholder="Введите новый пароль ещё раз"
              autoComplete="new-password"
              value={pw.again}
              onChange={(e) => setPw((p) => ({ ...p, again: e.target.value }))}
            />
          </div>
          <div className="sec-foot">
            <button className="btn btn-primary" id="pwSave" type="button" onClick={() => void changePassword()} disabled={pwBusy}>
              Изменить пароль
            </button>
          </div>
        </div>}

        {activeTab === 'nodes' && <div className="sec">
          <h2>
            <span className="sq" />
            Ноды и подготовка профилей
          </h2>
          <div className="field-row">
            <label htmlFor="stConcurrency">
              Стартовое число браузеров на ноду (авто-разгон +1 до предела, откат −2 при ошибке)
            </label>
            <input
              type="number"
              id="stConcurrency"
              min={0}
              max={200}
              value={form.concurrency}
              onChange={(e) => patchForm({ concurrency: e.target.value })}
            />
          </div>
          <div className="field-row">
            <label htmlFor="stFarmSplit">
              Распределение браузеров: Фарм {Math.max(0, Math.min(100, Number(form.farmSplitPct) || 0))}% · Таргеты{' '}
              {100 - Math.max(0, Math.min(100, Number(form.farmSplitPct) || 0))}%
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="range"
                id="stFarmSplit"
                min={0}
                max={100}
                step={5}
                value={form.farmSplitPct}
                onChange={(e) => patchForm({ farmSplitPct: e.target.value })}
                style={{ flex: 1 }}
              />
              <input
                type="number"
                min={0}
                max={100}
                value={form.farmSplitPct}
                onChange={(e) => patchForm({ farmSplitPct: e.target.value })}
                style={{ width: 72 }}
              />
            </div>
          </div>
          <div className="field-row">
            <label htmlFor="stRamPerBrowser">Память на один браузер, МБ</label>
            <input
              type="number"
              id="stRamPerBrowser"
              min={50}
              max={4000}
              step={50}
              value={form.ramPerBrowser}
              onChange={(e) => patchForm({ ramPerBrowser: e.target.value })}
            />
          </div>
          <div className="set-row">
            <div className="txt">
              <b>Постоянная подготовка профилей</b>
              <small>
                Если включено, нода после установки сразу начинает готовить профили и делает это без остановки. Пока включено
                управление на странице «Запуск», этим управляет она.
              </small>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                id="stContinuousFarm"
                checked={form.continuousFarm}
                disabled={autopilotLocked}
                title={autopilotLocked ? LOCK_FARM_TITLE : ''}
                onChange={(e) => patchForm({ continuousFarm: e.target.checked })}
              />
              <i />
            </label>
          </div>
        </div>}

        {activeTab === 'nodes' && <div className="sec">
          <h2>
            <span className="sq" />
            Плавные клики
          </h2>
          <div className="set-row">
            <div className="txt">
              <b>Растягивать дневной план на сутки</b>
              <small>
                Иначе квота уходит пачкой, как только есть свободные браузеры. С включённым темпом клики идут
                весь день: утро набирает, день и вечер гуще, ночь тише, с естественными провалами.
              </small>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                id="stClickPace"
                checked={form.clickPaceEnabled}
                onChange={(e) => patchForm({ clickPaceEnabled: e.target.checked })}
              />
              <i />
            </label>
          </div>
          {form.clickPaceEnabled && <>
            <div className="field-row">
              <label htmlFor="stPaceTz">Часовой пояс кривой</label>
              <select
                id="stPaceTz"
                value={form.clickPaceTimezone}
                onChange={(e) => patchForm({ clickPaceTimezone: e.target.value })}
              >
                {PACE_ZONES.map((z) => (
                  <option key={z.id} value={z.id}>{z.label}</option>
                ))}
                {!PACE_ZONES.some((z) => z.id === form.clickPaceTimezone) && (
                  <option value={form.clickPaceTimezone}>{form.clickPaceTimezone}</option>
                )}
              </select>
            </div>
            <div className="field-row">
              <label htmlFor="stPaceStart">Окно по местному времени, часы (0–24). Вне окна почти не кликает.</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="number"
                  id="stPaceStart"
                  min={0}
                  max={23}
                  value={form.clickPaceStartHour}
                  onChange={(e) => patchForm({ clickPaceStartHour: e.target.value })}
                  style={{ width: 72 }}
                />
                <span style={{ color: 'var(--text-3)' }}>→</span>
                <input
                  type="number"
                  id="stPaceEnd"
                  min={0}
                  max={24}
                  value={form.clickPaceEndHour}
                  onChange={(e) => patchForm({ clickPaceEndHour: e.target.value })}
                  style={{ width: 72 }}
                />
              </div>
            </div>
            <div className="field-row">
              <label htmlFor="stPaceStrength">
                Рельеф: {Number(form.clickPaceStrength) || 0}% (0 — ровно, 100 — ночь почти пустая, день и вечер пики)
              </label>
              <input
                type="range"
                id="stPaceStrength"
                min={0}
                max={100}
                step={5}
                value={form.clickPaceStrength}
                onChange={(e) => patchForm({ clickPaceStrength: e.target.value })}
              />
            </div>
            <div className="field-row">
              <label>Как ляжет сутки (без случайных провалов)</label>
              <div
                aria-hidden
                style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 56, paddingTop: 8 }}
              >
                {pacePreviewHeights(
                  Number(form.clickPaceStrength) || 0,
                  Number(form.clickPaceStartHour) || 0,
                  Number(form.clickPaceEndHour) || 24,
                ).map((h, i) => (
                  <span
                    key={i}
                    title={`${i}:00`}
                    style={{
                      flex: 1,
                      height: `${h}%`,
                      borderRadius: 2,
                      background: 'var(--accent, #5b8def)',
                      opacity: 0.55 + (h / 200),
                    }}
                  />
                ))}
              </div>
            </div>
          </>}
        </div>}

        {activeTab === 'nodes' && <div className="sec">
          <h2>
            <span className="sq" />
            Куки для целевых кликов
          </h2>
          <div className="set-row">
            <div className="txt">
              <b>Файлы Netscape</b>
              <small>
                Если есть куки, которые ещё ни разу не использовались, клик идёт с ними на новом профиле.
                Когда такие файлы кончаются, профили готовятся как сейчас.
                {cookieSummary
                  ? ` Свободно: ${cookieSummary.unused}. Уже использованы: ${cookieSummary.used}.`
                  : ''}
              </small>
            </div>
            <label className="btn btn-sm">
              {cookieBusy ? 'Загрузка…' : 'Загрузить'}
              <input
                id="cookieUpload"
                type="file"
                accept=".txt,.zip,text/plain,application/zip"
                multiple
                hidden
                disabled={cookieBusy}
                onChange={(event) => {
                  const selected = Array.from(event.target.files ?? []);
                  event.target.value = '';
                  void uploadCookies(selected);
                }}
              />
            </label>
          </div>
        </div>}

        {activeTab === 'telegram' && <div className="sec">
          <h2>
            <span className="sq" />
            Telegram-уведомления
          </h2>
          <div className="field-row">
            <label htmlFor="tgToken">Токен Telegram-бота</label>
            <input
              type="password"
              id="tgToken"
              autoComplete="off"
              spellCheck={false}
              placeholder={
                tokenSet ? 'Токен уже сохранён. Введите новый, только если хотите заменить его.' : 'Например: 123456789:AAH4kqZ…'
              }
              value={form.tgToken}
              onChange={(e) => patchForm({ tgToken: e.target.value })}
            />
          </div>
          <div className="field-row">
            <label htmlFor="tg">Telegram chat ID</label>
            <input
              type="text"
              id="tg"
              spellCheck={false}
              placeholder="Например: @mychannel или -1001234567890. Если оставить пустым, чат определится автоматически."
              value={form.tgChatId}
              onChange={(e) => patchForm({ tgChatId: e.target.value })}
            />
          </div>
          <div className="field-row">
            <label htmlFor="tgUsers">Кому бот отвечает на команды</label>
            <input
              type="text"
              id="tgUsers"
              spellCheck={false}
              placeholder="Например: 123456789, -1001234567890. Укажите ID людей, групп или каналов через запятую."
              value={form.tgUsers}
              onChange={(e) => patchForm({ tgUsers: e.target.value })}
            />
          </div>
          <div className="set-row" style={{ marginTop: 10 }}>
            <div className="txt">
              <b>Критические ошибки</b>
              <small>
                Бот напишет, если нода недоступна или зависла, закончились память, место на диске или прокси, не
                запускаются браузеры или панель перестала работать.
              </small>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                id="notifyCritical"
                checked={form.notifyCritical}
                onChange={(e) => patchForm({ notifyCritical: e.target.checked })}
              />
              <i />
            </label>
          </div>
          <div className="sec-foot">
            <span className="conn" id="tgStatus">
              {tgStatus}
            </span>
            <button className="btn" id="tgTest" type="button" onClick={() => void testTelegram()} disabled={tgBusy}>
              Проверить Telegram
            </button>
          </div>
        </div>}

        {activeTab === 'reset' && <div className="sec danger">
          <h2>
            <span className="sq" />
            <span style={{ color: 'var(--err)' }}>Опасная зона</span>
          </h2>
          <div className="set-row">
            <div className="txt">
              <b>Сбросить статистику</b>
              <small>Обнуляет счётчики успехов и ошибок по всем задачам на всех нодах. Сами задачи и настройки останутся.</small>
            </div>
            <button
              className="btn btn-sm btn-danger"
              id="resetStatsBtn"
              type="button"
              disabled={autopilotLocked}
              title={autopilotLocked ? LOCK_RESET_TITLE : ''}
              onClick={resetStats}
            >
              Сбросить
            </button>
          </div>
        </div>}
      </div>
    </section>
  );
}
